import React, { useCallback, useMemo, useState } from 'react';
import { WMButtonVariant, WMKeywordChips, WMSelect, WMTextField } from '@walkme/wm-ui';
import { create, InstanceProps } from 'react-modal-promise';
import IStore, { useSelector } from '../../../infrastructure/store.interface';
import StyledWMDialog from '../../Dialogs/StyledWMDialog/StyledWMDialog';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import classes from './CSVExportDialog.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
    CreateNotificationEngagementReportMutationVariables,
    CreateReportMutationVariables,
    DateRangeType,
    ReportParamsNew,
    ReportTypes,
    Language,
    useCreateNotificationEngagementReportMutation,
    useCreateReportMutation
} from '../../../../generated/apollo-hooks';

import { useStore } from 'react-redux';
import { enqueueSnackbar } from '../../../state/actions/ui';
import DateTimePicker from '../../EditNotification/EditNotificationContent/SendStepDrawer/DateTimePicker/DateTimePicker';
import { subtractDays } from '../../../services/utils';
import logger from '../../../services/logger.service';
import dayjs from 'dayjs';
import NoDataExistsDialog from './NoDataExistsDialog';
import { useElectronAnalytics } from '../../AppPreloading/hooks/settings/useUserSettings';
import { AnalyticsSource } from '@communication-center/data-objects';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import config from '../../../config/config';

const keyPrefix = 'components.notificationsList.notificationsDataExport.CSVExportDialog';

const dataTestId = 'csv-export-dialog';

const EmailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SPACE_KEY_CODE = 32;

const CSVExportDialog = ({
    onResolve,
    isOpen,
    close,
    withDateSelection = true,
    reportType,
    title,
    reportParams
}: InstanceProps<void> & {
    withDateSelection?: boolean;
    reportType: ReportTypes;
    title: string;
    reportParams?: ReportParamsNew;
}) => {
    const { t } = useTranslation('', { keyPrefix });
    const systemLanguage = useSelector(state => state.ui.languageCode);
    const language = useMemo(() => {
        if (systemLanguage === 'en') return Language.En;
        if (systemLanguage === 'ja') return Language.Ja;
        return Language.En;
    }, [systemLanguage]);

    const meRegex = useMemo(() => {
        const me = t('me');
        const regexp = `^${me}$`;
        return new RegExp(regexp, 'i');
    }, [t]);
    const defaultOption = useMemo(() => ({ value: DateRangeType.Last_90Days, label: t('last90days') }), [t]);
    const selectOptions = useMemo(
        () => [
            defaultOption,
            { value: DateRangeType.Last_30Days, label: t('last30days') },
            {
                value: DateRangeType.Last_7Days,
                label: t('last7days')
            },
            {
                value: DateRangeType.Custom,
                label: t('custom')
            }
        ],
        [defaultOption, t]
    );

    const getSchema = useCallback(
        (email?: string) => {
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
            const isValidEmail = EmailRegex.test(email);
            let yupEmailStringRule = yup.string().required(t('getSchema.emailRequired'));
            let defaultRecipients: string[];
            if (isValidEmail) {
                yupEmailStringRule = yupEmailStringRule.test('', t('getSchema.mustBeValidEmailOrMe'), value => {
                    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                    const isValidEmail = EmailRegex.test(value);
                    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                    const isMe = meRegex.test(value);
                    if (!isValidEmail && !isMe) {
                        return false;
                    }
                    return true;
                });
                defaultRecipients = [t('me')];
            } else {
                yupEmailStringRule = yupEmailStringRule.test('', t('getSchema.mustBeValidEmail'), value => {
                    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                    return EmailRegex.test(value);
                });
                defaultRecipients = [];
            }
            const schema = yup
                .object({
                    recipients: yup.array().of(yupEmailStringRule).required(t('getSchema.mustHaveFields')).min(1, t('getSchema.mustHaveRecipient')),
                    note: yup.string().max(200, t('getSchema.charLimit'))
                })
                .required();
            return { defaultRecipients, schema };
        },
        [meRegex, t]
    );

    const environmentId = useSelector(store => store.common.currentEnvironment.id);

    const { email } = useSelector(state => state.common.user);
    const { defaultRecipients, schema } = useMemo(() => getSchema(email), [email, getSchema]);

    const useWorkstationAnalytics = useElectronAnalytics();
    const analyticsSource = useMemo(() => {
        if (!isNil(useWorkstationAnalytics)) return useWorkstationAnalytics ? AnalyticsSource.Workstation : AnalyticsSource.Legacy;
        return null;
    }, [useWorkstationAnalytics]);

    const useFormMethods = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            dateInterval: withDateSelection ? defaultOption : null,
            startDate: withDateSelection ? subtractDays(new Date(), 30) : null,
            endDate: withDateSelection ? new Date() : null,
            recipients: defaultRecipients,
            note: ''
        },
        resolver: yupResolver(schema)
    });

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors }
    } = useFormMethods;
    const { dateInterval, endDate, startDate, note, recipients } = getValues();

    const baseVariables = {
        environmentId,
        note,
        recipients: recipients.map((it: string) => (meRegex.test(it) ? (email as string) : it)),
        language
    };
    let asyncReportVariables: CreateNotificationEngagementReportMutationVariables;
    let variables: CreateReportMutationVariables;
    if (reportType === ReportTypes.NotificationEngagement) {
        asyncReportVariables = { ...baseVariables, notificationInfo: reportParams!.notification! };
    } else {
        variables = { ...baseVariables, reportType, analyticsSource, timezoneOffset: new Date().getTimezoneOffset() };
        if (withDateSelection) {
            Object.assign(variables, { dateRange: { startDate, endDate, type: dateInterval!.value } });
        }
        if (reportParams) {
            Object.assign(variables, { reportParams });
        }
    }

    const context = useMemo(() => {
      if (reportType === ReportTypes.RecipientsList) {
        return { context: { timeout: config.recipientsListReportTimeout } };
      }
      return {};
    }, [reportType]);

    const [syncReportMutateFunction] = useCreateReportMutation({ variables: variables!, ...context });
    const [asyncReportMutateFunction] = useCreateNotificationEngagementReportMutation({ variables: asyncReportVariables! });

    const [dateStartEndShow, setDateStartEndShow] = useState(withDateSelection ? getValues('dateInterval')!.value === DateRangeType.Custom : false);
    const [isSaving, setIsSaving] = useState(false);
    const [displayNoDataDialog, setDisplayNoDataDialog] = useState(false);

    const store = useStore<IStore>();
    const sendReport = useCallback(async () => {
        setIsSaving(true);
        const showSnackbar = () => {
            close();
            store.dispatch(
                enqueueSnackbar({
                    message: t('snackbarSuccess'),
                    options: { variant: 'success' }
                })
            );
        };
        try {
            if (reportType !== ReportTypes.NotificationEngagement) {
                const { data } = await syncReportMutateFunction();
                const recordsSent = data?.createReport.recordsSent;
                if (recordsSent && recordsSent > 0) {
                    showSnackbar();
                } else {
                    setIsSaving(false);
                    setDisplayNoDataDialog(true);
                }
            } else {
                await asyncReportMutateFunction();
                showSnackbar();
            }
        } catch (error: $TSFixMe) {
            setIsSaving(false);
            logger.error('Could not create report', error);
            store.dispatch(
                enqueueSnackbar({
                    message: t('snackbarError'),
                    options: { variant: 'error' }
                })
            );
        }
    }, [reportType, syncReportMutateFunction, asyncReportMutateFunction, close, store, t]);

    const getMinDate = () => {
        return dayjs().subtract(1, 'year').toDate();
    };

    const startAtDisabledDates = (day: Date) => {
        const { endDate } = getValues();
        return day < getMinDate() || day > endDate!;
    };

    const endAtDisabledDates = (day: Date) => {
        const { startDate } = getValues();
        return day < startDate! || day > new Date();
    };

    const handleDateIntervalChange = (v: $TSFixMe) => {
        setValue('dateInterval', v);
        if (v.value === DateRangeType.Custom) {
            setDateStartEndShow(true);
        } else {
            setDateStartEndShow(false);
        }
    };

    const getDefaultProps = useMemo(
        () => ({
            value: defaultRecipients,
            onChange: (chips: string[]) => {
                setValue('recipients', chips, { shouldValidate: true });
            }
        }),
        [defaultRecipients, setValue]
    );

    const startDateMonthFrom = getMinDate();
    const [startDateMonthTo, setStartDateMonthTo] = useState(endDate!);

    const endDateMonthTo = new Date();
    const [endDateMonthFrom, setEndDateMonthFrom] = useState(endDate!);

    const onDateEndChange = (date: Date) => {
        setValue('endDate', date);
        setStartDateMonthTo(date);
    };
    const onDateStartChange = (date: Date) => {
        setValue('startDate', date);
        setEndDateMonthFrom(date);
    };

    const onDialogClose = () => {
        onResolve();
    };

    const chipProps = useMemo(() => ({ classes: { root: classes.chipRoot, label: classes.chipLabel } }), []);

    const keywordChipsClasses = useMemo(
        () => ({
            chipContainer: classes.keywordChipsContainer,
            root: classes.keywordChipsRoot
        }),
        []
    );

    return (
        <FormProvider {...useFormMethods}>
            {displayNoDataDialog ? (
                <NoDataExistsDialog isOpen close={() => setDisplayNoDataDialog(false)} />
            ) : (
                <StyledWMDialog
                    title={title}
                    open={isOpen}
                    onClose={onDialogClose}
                    buttons={[
                        {
                            children: t('cancel'),
                            variant: WMButtonVariant.Secondary,
                            className: classes.cancelButton,
                            disabled: isSaving,
                            onClick: onDialogClose,
                            ['data-testid' as $TSFixMe]: `${dataTestId}-discard`
                        },
                        {
                            children: t('export'),
                            variant: WMButtonVariant.Primary,
                            loading: isSaving,
                            disabled: Object.keys(errors).length > 0,
                            type: 'submit',
                            ['data-testid' as $TSFixMe]: `${dataTestId}-confirm`
                        }
                    ]}
                    className={classes.dialogRoot}
                    classes={{ content: classes.dialogContent }}
                    data-testid={dataTestId}
                    onSubmit={handleSubmit(sendReport)}
                    PaperComponent={'form' as $TSFixMe}
                >
                    <div>{t('text')}</div>

                    {withDateSelection && (
                        <div className={classes.dateRangeContainer}>
                            <span className={classes.fieldTitle}>{t('dateRange')}</span>
                            <Controller
                                name="dateInterval"
                                render={() => (
                                    <WMSelect
                                        defaultValue={defaultOption}
                                        openMenuOnFocus
                                        isDisabled={isSaving}
                                        placeholder={t('selectProperty')}
                                        isSearchable={false}
                                        options={selectOptions}
                                        onChange={handleDateIntervalChange}
                                    />
                                )}
                            />
                        </div>
                    )}

                    {dateStartEndShow && (
                        <div className={classes.customDateIntervalContainer}>
                            <div className={classes.dateSelection}>
                                <span className={classes.fieldTitle}>{t('startDate')}</span>
                                <Controller
                                    control={control}
                                    name="startDate"
                                    render={({ field: { value } }) => (
                                        <DateTimePicker
                                            value={value!}
                                            disabled={isSaving}
                                            onDateTimeChange={onDateStartChange}
                                            disabledDays={startAtDisabledDates}
                                            monthAndYearSelectionFromMonth={startDateMonthFrom}
                                            monthAndYearSelectionToMonth={startDateMonthTo}
                                        />
                                    )}
                                />
                            </div>

                            <div className={classes.dateSelection}>
                                <span className={classes.fieldTitle}>{t('endDate')}</span>
                                <Controller
                                    control={control}
                                    name="endDate"
                                    render={({ field: { value } }) => (
                                        <DateTimePicker
                                            value={value!}
                                            disabled={isSaving}
                                            onDateTimeChange={onDateEndChange}
                                            monthAndYearSelectionFromMonth={endDateMonthFrom}
                                            monthAndYearSelectionToMonth={endDateMonthTo}
                                            disabledDays={endAtDisabledDates}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    )}
                    <div className={classes.addRecipientsContainer}>
                        <span className={classes.fieldTitle}>{t('addRecipients')}</span>
                        <Controller
                            name="recipients"
                            render={({ formState: { errors } }) => {
                                const recipientsErrorMsg =
                                    errors?.recipients?.message ||
                                    (Array.isArray(errors.recipients) && errors.recipients.find(it => it?.message)?.message);
                                return (
                                    <WMKeywordChips
                                        {...getDefaultProps}
                                        helperText={
                                            <div className={recipientsErrorMsg ? classes.addRecipientsError : classes.addRecipientsHelperText}>
                                                {recipientsErrorMsg || t('useSpace')}
                                            </div>
                                        }
                                        classes={keywordChipsClasses}
                                        FormHelperTextProps={{ classes: { error: classes.helperTextError } }}
                                        error={!!recipientsErrorMsg}
                                        newChipKeyCodes={[SPACE_KEY_CODE]}
                                        allowDuplicates={false}
                                        disabled={isSaving}
                                        fullWidth
                                        blurBehavior="add"
                                        chipProps={chipProps}
                                        placeholder={t('typeEmailOrMe')}
                                    />
                                );
                            }}
                        />
                    </div>
                    <div className={classes.noteContainer}>
                        <Controller
                            name="note"
                            render={({ field: { value, onChange } }) => {
                                return <WMTextField ds2 value={value} disabled={isSaving} multiline onChange={onChange} placeholder={t('addNote')} />;
                            }}
                        />
                    </div>
                </StyledWMDialog>
            )}
        </FormProvider>
    );
};

CSVExportDialog.openModal = create(CSVExportDialog);

export default CSVExportDialog;
